import { RefreshCw } from 'lucide-react';
import type { PackBuilderGroup } from '../../../lib/api/operatorConsole';
import { Button } from '../../../components/ui/Button';
import { NumberCell } from './NumberCell';

interface Props {
  groups: PackBuilderGroup[];
  price: number;
  disabled: boolean;
  resetting: boolean;
  onChange: (groups: PackBuilderGroup[]) => void;
  onReset: () => void;
}

const headCell =
  'px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-[var(--co-muted)]';
const cell = 'px-3 py-1.5 text-[12px] text-[var(--co-text)]';

/**
 * The odds ladder as a table. Buckets stay contiguous: editing a row's `to`
 * moves the next row's `from` and vice versa, so the operator can never
 * produce a gap or an overlap. The last bucket is open-ended by default and is
 * the only one whose `to` may be cleared back to that.
 *
 * Min % is the group's solver floor: the smallest share of pulls this bucket
 * must get, empty meaning none. There is no per-bucket cap, as in the admin
 * form — an ambitious ask is the solver's business, and it either scales the
 * floors down together (`relaxed_floors`) or reports the EV as unreachable.
 * Only arithmetic nonsense is stopped here, mirroring the backend's
 * FLOORS_INVALID: a share outside 0-100%, or minimums claiming every pull.
 */
export function GroupsTable({ groups, price, disabled, resetting, onChange, onReset }: Props) {
  const last = groups.length - 1;
  // Backend GROUPS_NO_PRICE_EDGE compares boundaries with the whole-number part
  // of the price (`GroupsTemplate.price_edge`), so 25.50 is satisfied by a 25 edge.
  const priceEdge = Math.floor(price);
  const hasPriceEdge = groups.some((g) => g.from === priceEdge);
  // Minimums that together claim every pull leave the solver nothing to
  // distribute; the backend answers FLOORS_INVALID, so the table says it first.
  const floorsTotal = groups.reduce((sum, g) => sum + (g.floor ?? 0), 0);
  const floorsOverbooked = floorsTotal >= 100;

  function updateFrom(index: number, value: number | null) {
    if (value === null) return;
    const next = groups.map((g, i) => (i === index ? { ...g, from: value } : g));
    const previous = next[index - 1];
    if (previous) next[index - 1] = { ...previous, to: value };
    onChange(next);
  }

  // Only the last row may go back to null ("no limit"); a cleared boundary in
  // the middle would open a gap, so those cells are not clearable at all.
  function updateTo(index: number, value: number | null) {
    const next = groups.map((g, i) => (i === index ? { ...g, to: value } : g));
    const following = next[index + 1];
    if (following && value !== null) next[index + 1] = { ...following, from: value };
    onChange(next);
  }

  function updateFloor(index: number, value: number | null) {
    onChange(groups.map((g, i) => (i === index ? { ...g, floor: value } : g)));
  }

  return (
    <section className="rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)]">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--co-border)] px-4 py-2">
        <div>
          <h2 className="text-[13px] font-semibold text-[var(--co-text)]">Buckets</h2>
          <p className="text-[11px] text-[var(--co-muted)]">
            Card value ranges the odds are solved over. One boundary must equal the whole-number
            price. Min % is the smallest share of pulls a bucket must get — leave it empty to let
            the solver decide. Minimums must add up to less than 100%; ask for more than the pack
            can pay for and the solver scales them down together or reports the target as
            unreachable.
          </p>
        </div>
        <Button
          variant="ghost"
          onClick={onReset}
          disabled={disabled || resetting}
          className="gap-2 px-3 py-1 text-[12px]"
        >
          <RefreshCw size={12} aria-hidden="true" className={resetting ? 'animate-spin' : ''} />
          Reset Price Buckets
        </Button>
      </div>

      {!hasPriceEdge && (
        <p className="border-b border-[var(--co-border)] px-4 py-2 text-[11px] text-[var(--co-warning)]">
          No bucket starts at the price — one boundary must equal the whole-number price (
          {priceEdge}), or the validator will reject this ladder.
        </p>
      )}

      {floorsOverbooked && (
        <p className="border-b border-[var(--co-border)] px-4 py-2 text-[11px] text-[var(--co-error)]">
          Minimums add up to {Math.round(floorsTotal * 10) / 10}% — they must stay under 100%,
          otherwise there is nothing left for the solver to distribute.
        </p>
      )}

      <table aria-label="Buckets" className="w-full border-collapse">
        <thead>
          <tr className="border-b border-[var(--co-border)]">
            <th scope="col" className={headCell}>
              #
            </th>
            <th scope="col" className={headCell}>
              From
            </th>
            <th scope="col" className={headCell}>
              To
            </th>
            <th scope="col" className={headCell}>
              Min %
            </th>
          </tr>
        </thead>
        <tbody>
          {groups.map((group, index) => {
            const isLast = index === last;
            const inverted = group.to !== null && group.to <= group.from;
            return (
              <tr key={index} className="border-b border-[var(--co-border)] last:border-b-0">
                <td className={`${cell} font-mono text-[var(--co-muted)]`}>{index + 1}</td>
                <td className={cell}>
                  <NumberCell
                    aria-label={`Bucket ${index + 1} from`}
                    value={group.from}
                    min={0}
                    step="0.01"
                    disabled={disabled}
                    invalid={inverted}
                    onCommit={(v) => updateFrom(index, v)}
                  />
                </td>
                <td className={cell}>
                  {/* The top bucket takes a ceiling, and an empty cell means it
                      has none. A ceiling below the dearest card leaves that card
                      outside every bucket, where it can never be pulled — the
                      validator answers CARDS_OUT_OF_BUCKETS and the health panel
                      names the cards. */}
                  <NumberCell
                    aria-label={`Bucket ${index + 1} to`}
                    value={group.to}
                    min={0}
                    step="0.01"
                    disabled={disabled}
                    invalid={inverted}
                    clearable={isLast}
                    {...(isLast ? { placeholder: 'no limit' } : {})}
                    onCommit={(v) => updateTo(index, v)}
                  />
                </td>
                <td className={cell}>
                  <NumberCell
                    aria-label={`Bucket ${index + 1} min share`}
                    value={group.floor}
                    min={0}
                    max={100}
                    step="0.1"
                    disabled={disabled}
                    invalid={floorsOverbooked && (group.floor ?? 0) > 0}
                    clearable
                    placeholder="none"
                    onCommit={(v) => updateFloor(index, v)}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}
